import { useIsFocused, useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { ScrollView, View, type LayoutRectangle } from 'react-native'

import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useHorizontalWheelScroll } from '@/src/general/hooks/useHorizontalWheelScroll'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { cn } from '@/src/general/utils/cn'

import { Icon } from '../media/Icon'
import { Pressable } from '../pressable'
import { Typography } from '../text/Typography'
import type { TSliderProps } from './Slider'
import { getAutoScrollTarget, setLayoutAtIndex } from './Slider.utils'

const SLDIER_GAP = 16
export const MobileSlider = <TItem,>({
  title,
  color = 'default',
  list,
  renderItem,
  renderPlaceholderItem,
  getItemKey,
  link,
  testID,
  emptyState,
  maxNbItems = 10,
  classes,
  linkButton,
  autoScrollIndex,
  containerProps: { className: containerClassName, ...containerProps } = {},
}: TSliderProps<TItem>): ReactNode => {
  const [coordinates, setCoordinates] = useState<LayoutRectangle[]>([])
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false)
  const { navigate } = useRouter()
  const { scrollViewRef, enableScroll } = useHorizontalWheelScroll()
  const isFocused = useIsFocused()

  // Allow auto-scroll again when the target index changes (e.g. after a new publication is prayed).
  useOnChange(autoScrollIndex, (prevAutoScrollIndex) => {
    if (typeof prevAutoScrollIndex === 'number') {
      setHasAutoScrolled(false)
    }
  })

  const listWithLimitedItems = list?.slice(0, maxNbItems) || []

  const onItemLayout = (index: number, layout: LayoutRectangle) => {
    setCoordinates((prev) => setLayoutAtIndex(prev, index, layout))
  }
  useEffect(() => {
    const autoScrollTarget = getAutoScrollTarget({
      autoScrollIndex,
      coordinates,
      itemCount: listWithLimitedItems.length,
    })

    if (
      hasAutoScrolled ||
      !isFocused ||
      !autoScrollTarget ||
      !autoScrollTarget.coordinate.width ||
      !scrollViewRef.current
    ) {
      return
    }

    scrollViewRef.current.scrollTo({
      x: autoScrollIndex === 0 ? 0 : autoScrollTarget.coordinate.x - SLDIER_GAP,
      animated: true,
    })
    setHasAutoScrolled(true)
  }, [autoScrollIndex, coordinates, hasAutoScrolled, listWithLimitedItems.length, isFocused, scrollViewRef])

  const sliderContent = (
    <View
      className={cn('mb-4 flex flex-row gap-5 px-6 py-4', classes?.container, containerClassName)}
      {...containerProps}
    >
      {!list
        ? Array.from({ length: 8 }, (_, index) => renderPlaceholderItem(index))
        : listWithLimitedItems.map((item, index) => (
            <View
              key={getItemKey(item)}
              className={classes?.slot}
              onLayout={(event) => onItemLayout(index, event.nativeEvent.layout)}
              testID={testID ? `${testID}-item-${index}` : undefined}
            >
              {renderItem(item, index)}
            </View>
          ))}
    </View>
  )

  return (
    <View testID={testID} className={cn('w-full', classes?.base)}>
      <View className={cn('w-full px-6 pt-8', classes?.titleContainer)}>
        <Pressable onPress={() => (link ? navigate(link) : null)}>
          <View
            className={cn(
              'native:gap-2 flex w-full flex-row items-center web:justify-between web:gap-4',
              classes?.title,
            )}
          >
            <Typography colors={color} heading="h1" bold contrast="default" size="md">
              {title}
            </Typography>
            {linkButton}
            {link && !linkButton && (
              <Icon
                testID={TEST_ID.MISCELLANEOUS.ICON_CHEVRON_RIGHT}
                name={ICON.CHEVRON_RIGHT}
                size="md"
                className="native:ml-1 web:ml-4"
              />
            )}
          </View>
        </Pressable>
      </View>
      {list?.length === 0 && <View className="mt-6">{emptyState}</View>}
      {list?.length === 1 && sliderContent}
      {(!list || list.length > 1) && (
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          decelerationRate="fast"
          pagingEnabled
          onPointerEnter={enableScroll}
          className={classes?.slider}
          testID={testID ? `${testID}-scroll-view` : undefined}
        >
          {sliderContent}
        </ScrollView>
      )}
    </View>
  )
}
